import { useContext, useEffect } from "react";
import { useWatch } from "react-hook-form";
import { Alert, Box } from "@mui/material";
import { RecordContext } from "../../components/record/Record";
import { FormatedFields } from "../../types/fields.types";
import { Choice, getChoiceLabel } from "../../utils/choiceUtils";
import { hasValue } from "./dynamicTasksOptions";

// Live pt-PT recap of the rule, built from the trigger selections. The task always fires on the
// selected collection's status change; each additional trigger field is an extra AND condition.
export const TriggerSummary = () => {
	const context = useContext(RecordContext);
	const entityForm = context?.entityForm;
	const item = context?.item;
	const formatedFields = context?.formatedFields;
	const isDetail = context?.type === "detail";

	useWatch({ control: entityForm?.control });
	const value = (name: string) => {
		if (isDetail) return item?.[name];
		const formValue = entityForm?.getValues(name);
		return formValue === undefined ? item?.[name] : formValue;
	};

	const targetCollection = value("target_collection");

	let phrase = "";
	if (targetCollection === "sessions") {
		phrase = buildSessionsPhrase(
			formatedFields,
			value("trigger_session_status"),
			value("trigger_session_number"),
			value("trigger_sessions_service"),
			value("trigger_sessions_service_exclusion")
		);
	} else if (targetCollection === "treatments") {
		phrase = buildTreatmentsPhrase(
			formatedFields,
			value("trigger_treatment_status"),
			value("trigger_treatment_provided_services"),
			value("trigger_treatment_techs")
		);
	}

	// Before a collection is picked there is no phrase: render the same alert invisibly so the slot
	// keeps its height and the layout doesn't jump when the recap appears.
	return (
		<Box sx={{ px: 4, pt: 2 }}>
			<Alert severity="info" sx={phrase ? undefined : { visibility: "hidden" }}>
				{phrase || "Selecione o Registo âncora."}
			</Alert>
		</Box>
	);
};

const getChoiceText = (formatedFields: FormatedFields | undefined, fieldName: string, value: any) => {
	const field = formatedFields?.find((formatedField) => formatedField.field === fieldName);
	return getChoiceLabel((field?.settings?.options?.choices ?? []) as Array<Choice>, value);
};

const resolveRelatedDisplay = (value: any) => {
	if (!hasValue(value)) return null;
	if (typeof value === "object") {
		const label = value.label ?? value.name ?? value.item?.name;
		return label ? `"${label}"` : "selecionado";
	}
	return "selecionado";
};

// The status is mandatory, so the clause always names it — a placeholder while none is chosen.
const statusClause = (formatedFields: FormatedFields | undefined, fieldName: string, status: any) =>
	`para o estado "${hasValue(status) ? getChoiceText(formatedFields, fieldName, status) : "Estado por Selecionar"}"`;

const buildSessionsPhrase = (
	formatedFields: FormatedFields | undefined,
	status: any,
	number: any,
	service: any,
	serviceExclusion: any
) => {
	const base = `Uma Tarefa será criada sempre que uma Sessão mudar ${statusClause(formatedFields, "trigger_session_status", status)}`;
	const serviceDisplay = resolveRelatedDisplay(service);
	const serviceExclusionDisplay = resolveRelatedDisplay(serviceExclusion);

	const clauses: Array<string> = [];
	if (hasValue(number) && serviceDisplay) clauses.push(`essa sessão for a ${number}ª do serviço ${serviceDisplay}`);
	else if (hasValue(number)) clauses.push(`essa sessão for a ${number}ª`);
	else if (serviceDisplay) clauses.push(`o serviço for ${serviceDisplay}`);
	const exclusionClause = serviceExclusionDisplay
		? `, e não existir nenhuma sessão (de agendada a realizada) do serviço ${serviceExclusionDisplay} no tratamento/lead`
		: "";

	return `${base}${clauses.map((clause) => ` e ${clause}`).join("")}${exclusionClause}.`;
};

const buildTreatmentsPhrase = (formatedFields: FormatedFields | undefined, status: any, services: any, techs: any) => {
	const base = `Uma tarefa será criada sempre que um Tratamento mudar ${statusClause(formatedFields, "trigger_treatment_status", status)}`;
	const clauses: Array<string> = [];

	// trigger_treatment_provided_services is a single select whose values encode the condition
	// (only_/contain_/both variants) — the clause shows the chosen condition's label.
	if (hasValue(services)) {
		clauses.push(
			`os serviços prestados corresponderem a "${getChoiceText(formatedFields, "trigger_treatment_provided_services", services)}"`
		);
	}
	if (hasValue(techs)) {
		const techConditionLabels = techs.map((tech: string) => getChoiceText(formatedFields, "trigger_treatment_techs", tech));
		clauses.push(...techConditionLabels);
	}

	return `${base}${clauses.map((clause) => ` e ${clause}`).join("")}.`;
};
